// Every image the landing uses, and two small components to show them.
//
// The choices, and why, are at the top of Landing.js.
//
// PHOTOS are the four hero photographs (Assets/hero, full srcSet from 1280 to
// 4K plus a portrait set) and the night image. CAPTURES are the product screens,
// resized from Assets/product/*.png to 560 and 1120 wide: the largest slot is a
// 320px tall card (510 wide) at 1920, and the originals are 3120 wide and 400 KB.
// product-customers.png is deliberately absent: it shows real-looking phone
// numbers. Do not add it.

import HERO_PHOTOS from "../Home/heroPhotos";

import night1280 from "./img/night-1280.webp";
import night1920 from "./img/night-1920.webp";
import nightP828 from "./img/night-p828.webp";

import board560 from "./img/capture-board-560.webp";
import board1120 from "./img/capture-board-1120.webp";
import jobs560 from "./img/capture-jobs-560.webp";
import jobs1120 from "./img/capture-jobs-1120.webp";
import stock560 from "./img/capture-stock-560.webp";
import stock1120 from "./img/capture-stock-1120.webp";
import finance560 from "./img/capture-finance-560.webp";
import finance1120 from "./img/capture-finance-1120.webp";
import invoices560 from "./img/capture-invoices-560.webp";
import invoices1120 from "./img/capture-invoices-1120.webp";

export const PHOTOS = {
    ...HERO_PHOTOS,
    night: {
        srcSet: `${night1280} 1280w, ${night1920} 1920w`,
        portraitSrcSet: `${nightP828} 828w`,
        src: night1920,
    },
};

// What a smaller screen may choose from. A browser picks by drawn width times pixel
// density, and a full-bleed photo is drawn wider than the window, so a 3x phone on
// its side would otherwise ask for a 4K file to fill 844 CSS pixels. Below the desktop
// layout the list stops short: 1920 on a phone, either way up, and 2560 on a tablet.
const PORTRAIT_PHONE = "(max-width: 720px) and (orientation: portrait)";
const PHONE = "(max-width: 720px), (max-width: 1023px) and (max-height: 500px)";
const TABLET = "(max-width: 1023px)";
const upTo = (srcSet, max) =>
    srcSet.split(", ").filter((entry) => parseInt(entry.split(" ").pop(), 10) <= max).join(", ");

const capture = (small, large, alt) => ({
    src: large,
    srcSet: `${small} 560w, ${large} 1120w`,
    width: 1120,
    height: 704,
    alt,
});

export const CAPTURES = {
    board: capture(board560, board1120, "شاشة بورد الريّس"),
    jobs: capture(jobs560, jobs1120, "شاشة المشاوير في الريّس"),
    stock: capture(stock560, stock1120, "شاشة العهدة في الريّس"),
    finance: capture(finance560, finance1120, "شاشة محرّك المحاسبة في الريّس"),
    invoices: capture(invoices560, invoices1120, "شاشة الفواتير في الريّس"),
};

// A full-bleed or tile photograph. Lazy unless `eager`. Pass `portrait={false}`
// to a tile that is not tall on a phone, so it keeps the landscape crop.
// Decorative by default; pass `alt` when it carries meaning.
export function Photo({ name, sizes = "100vw", alt = "", eager = false, portrait = true, className = "ld-photo", style }) {
    const ph = PHOTOS[name];
    if (!ph) return null;
    return (
        <picture className={className} style={style}>
            {portrait ? (
                <source media={PORTRAIT_PHONE} srcSet={ph.portraitSrcSet} sizes={sizes} />
            ) : null}
            <source media={PHONE} srcSet={upTo(ph.srcSet, 1920)} sizes={sizes} />
            <source media={TABLET} srcSet={upTo(ph.srcSet, 2560)} sizes={sizes} />
            <img
                src={ph.src}
                srcSet={ph.srcSet}
                sizes={sizes}
                width={1920}
                height={1080}
                alt={alt}
                loading={eager ? "eager" : "lazy"}
                decoding="async"
                fetchPriority={eager ? "high" : undefined}
            />
        </picture>
    );
}

// A product screen. Its alt says what it is unless the card beside it already
// does, in which case pass alt="".
export function Capture({ name, sizes = "(min-width: 1024px) 30vw, 85vw", alt, className = "ld-photo", style }) {
    const c = CAPTURES[name];
    if (!c) return null;
    return (
        <img
            className={className}
            style={style}
            src={c.src}
            srcSet={c.srcSet}
            sizes={sizes}
            width={c.width}
            height={c.height}
            alt={alt ?? c.alt}
            loading="lazy"
            decoding="async"
        />
    );
}
